/**
 * Access layer for the reviews feed.
 *
 * The upstream API serves a fixed 20 reviews per page via `?p=N`, ordered
 * newest first, and gives no grand total — its `total` field is just the number
 * of items in that one response. Pages beyond the last return an empty array.
 * So the total has to be discovered, see discoverTotal().
 */

export const API_PAGE_SIZE = 20;

/** Stop doubling here; a runaway probe should not hammer the API. */
const MAX_PROBE_PAGE = 4096;

const TOTAL_CACHE_PREFIX = 'schipholtaxi:reviewsTotal:';
const TOTAL_CACHE_TTL_MS = 6 * 60 * 60 * 1000; // 6 hours

/** key -> Promise<{reviews, averageRating}>; also de-duplicates in-flight calls. */
const pageCache = new Map();

const withPageParam = (endpoint, page) => {
  const url = new URL(endpoint, window.location.origin);
  url.searchParams.set('p', String(page));

  return url.toString();
};

const requestPage = async (endpoint, page) => {
  const response = await fetch(withPageParam(endpoint, page));

  if (!response.ok) {
    throw new Error(`Failed to fetch reviews: ${response.statusText}`);
  }

  const data = await response.json();

  // Handle different API response formats
  const raw = data.reviews || data.result || data;

  return {
    reviews: Array.isArray(raw) ? raw : [],
    averageRating: data.average_rating ?? null,
  };
};

/**
 * One API page (1-based), memoised for the lifetime of the document. A failed
 * request is evicted so a later attempt can retry.
 */
export const fetchApiPage = (endpoint, page) => {
  const key = `${endpoint}|${page}`;

  if (!pageCache.has(key)) {
    pageCache.set(
      key,
      requestPage(endpoint, page).catch((err) => {
        pageCache.delete(key);
        throw err;
      })
    );
  }

  return pageCache.get(key);
};

/**
 * Which API pages cover one display page, and where the slice starts inside
 * them. Spans two API pages when perPage does not divide API_PAGE_SIZE evenly.
 */
export const apiPagesFor = (page, perPage, pageSize = API_PAGE_SIZE) => {
  const offset = (page - 1) * perPage;
  const firstPage = Math.floor(offset / pageSize) + 1;
  const lastPage = Math.floor((offset + perPage - 1) / pageSize) + 1;

  return {
    offset,
    firstPage,
    lastPage,
    startWithin: offset - (firstPage - 1) * pageSize,
  };
};

/** Reviews for one display page, plus what we learned about there being more. */
export const fetchDisplayPage = async (endpoint, page, perPage) => {
  const { offset, firstPage, lastPage, startWithin } = apiPagesFor(page, perPage);

  const wanted = [];
  for (let p = firstPage; p <= lastPage; p++) {
    wanted.push(p);
  }

  // In parallel: a large perPage spans several API pages and they don't depend
  // on each other. fetchApiPage de-duplicates, so overlap costs nothing.
  const pages = await Promise.all(wanted.map((p) => fetchApiPage(endpoint, p)));

  const window = pages.flatMap((result) => result.reviews);
  const reviews = window.slice(startWithin, startWithin + perPage);
  const lastWasFull = pages[pages.length - 1].reviews.length === API_PAGE_SIZE;

  return {
    reviews,
    averageRating: pages[0].averageRating,
    offset,
    // Only a guess until the total is known: the page after a full API page may
    // still turn out to be empty.
    mayHaveMore:
      reviews.length === perPage &&
      (window.length > startWithin + perPage || lastWasFull),
  };
};

const readCachedTotal = (endpoint) => {
  try {
    const raw = window.localStorage.getItem(TOTAL_CACHE_PREFIX + endpoint);
    if (!raw) {
      return null;
    }

    const { total, ts } = JSON.parse(raw);

    if (typeof total !== 'number' || Date.now() - ts > TOTAL_CACHE_TTL_MS) {
      return null;
    }

    return total;
  } catch {
    return null;
  }
};

const writeCachedTotal = (endpoint, total) => {
  try {
    window.localStorage.setItem(
      TOTAL_CACHE_PREFIX + endpoint,
      JSON.stringify({ total, ts: Date.now() })
    );
  } catch {
    // Private mode or full storage — losing the cache is not fatal.
  }
};

/**
 * Total number of reviews. The API can't tell us, so find the last non-empty
 * page by doubling until a page comes back empty and then bisecting. Roughly a
 * dozen requests, all of them memoised by fetchApiPage and the result cached in
 * localStorage for 6 hours — the feed grows by a handful of reviews a day, so a
 * slightly stale total is harmless.
 */
export const discoverTotal = async (endpoint) => {
  const cached = readCachedTotal(endpoint);
  if (cached !== null) {
    return cached;
  }

  // Bracket the last page: lo has reviews (verified at the end), hi is empty.
  let lo = 1;
  let hi = null;
  let probe = 16;

  while (hi === null) {
    const { reviews } = await fetchApiPage(endpoint, probe);

    if (reviews.length === 0) {
      hi = probe;
    } else if (probe >= MAX_PROBE_PAGE) {
      hi = probe + 1;
      lo = probe;
    } else {
      lo = probe;
      probe *= 2;
    }
  }

  while (hi - lo > 1) {
    const mid = Math.floor((lo + hi) / 2);
    const { reviews } = await fetchApiPage(endpoint, mid);

    if (reviews.length === 0) {
      hi = mid;
    } else {
      lo = mid;
    }
  }

  // lo is the last page that might hold reviews; if the feed is empty this
  // resolves to page 1 returning nothing, giving a total of 0.
  const last = await fetchApiPage(endpoint, lo);
  const total = (lo - 1) * API_PAGE_SIZE + last.reviews.length;

  writeCachedTotal(endpoint, total);

  return total;
};
